import { Sha256 }  from '@aws-crypto/sha256-browser';
import md5 from 'spark-md5';
import Helper from './Helper';

const VERSION = "2.3.0";

/**
 * @typedef {Object} LinguiseRemoteDynamicConfig The Linguise dynamic translations configuration
 * @property {boolean} enabled If the dynamic translations are enabled
 * 
 * @typedef {Object} LinguiseRemoteLanguageConfig The Linguise language configuration
 * @property {string} code The language code
 * @property {string} name The language name
 * @property {string} original_name The language original name
 * 
 * @typedef {Object} LinguiseRemoteRulesConfig The Linguise rules configuration
 * @property {1 | 2 | 3 | 4 | 5 | 6} type The rule type
 * @property {Object} params The rule parameters
 * @property {boolean} apply_to_url If the rule should be applied to the current URL
 * @property {boolean} apply_to_language If the rule should be applied to the current language
 *
 * @typedef {Object} LinguiseRemoteConfig The Linguise configuration
 * @property {string} domain The full URL used when requesting the page
 * @property {string} url The actual domain URL registered
 * @property {string} language The default language for the domain
 * @property {boolean} translate_urls If the URLs should be translated
 * @property {LinguiseRemoteDynamicConfig} dynamic_translations Dynamic translations configuration
 * @property {Object} language_settings the domain flags settings
 * @property {LinguiseRemoteLanguageConfig[]} languages The available languages
 * @property {"subdomains" | "subfolders"} structure The domain structure
 * @property {string} platform The platform used
 * @property {boolean} debug If the debug mode is enabled
 * @property {string} public_key The public key for this domain
 * @property {LinguiseRemoteRulesConfig[]} rules The rules for this domain
 */

/**
 * Communication class to handle all the API calls
 * 
 * @class Communication
 */
class Communication {
    constructor() {
        this._cached_config = null;

        this._stored_pkey = null;
        this.linguiseFetch = window.fetch.bind(window);

        if (document.currentScript) {
            const url = new URL(document.currentScript.src);
            this._stored_pkey = url.searchParams.get('d');
        }

        this._config_request = false;

        if (typeof originalFetch !== 'undefined' && typeof originalFetch === 'function') {
            this.linguiseFetch = originalFetch.bind(window);
        }
    }

    /**
     * Set the internal public key to be used
     * @param {string | null} public_key Public key
     * @returns
     */
    setPublicKey(public_key) {
        if (!public_key && this._stored_pkey) {
            // keep the stored key
            return;
        }
        this._stored_pkey = public_key;
    }

    _waitUntilConfigUnlock() {
        return new Promise((resolve) => {
            const interval = setInterval(() => {
                if (!this._config_request) {
                    clearInterval(interval);
                    resolve();
                }
            }, 100);
        });
    }

    /**
     * Get the extra metadata from the script tag.
     * 
     * This would allow faster loading of the configuration.
     * 
     * @returns {LinguiseRemoteConfig}
     */
    getStoredMetadata() {
        // Check HTML content in <head> for `linguise-extra-metadata` script tag
        const extra_metadata = document.querySelector('script#linguise-extra-metadata');
        if (extra_metadata) {
            // Content type is application/json, extract and parse
            const content = JSON.parse(extra_metadata.textContent);

            if (!content['url'].startsWith('http')) {
                // Decode base64 encoded URL
                content['url'] = atob(content['url']);
                content['domain'] = atob(content['domain']);
            }

            return content;
        }
    }

    /**
     * Retrieve domain configuration from Linguise API
     *
     * @param domain_id
     *
     * @returns {Promise<any>}
     */
    async getConfig(domain_id)
    {
        // wait for the previous request to finish
        if (this._config_request) {
            await this._waitUntilConfigUnlock();
        }
        this._config_request = true;

        if (this._cached_config !== null) {
            return this._cached_config;
        }

        const storedMeta = this.getStoredMetadata();
        if (storedMeta) {
            this._cached_config = storedMeta;
            this._stored_pkey = storedMeta.public_key;
            this._config_request = false;
            return storedMeta;
        }

        if (!this._stored_pkey) {
            throw new Error('Linguise: No available key can be used');
        }

        try {
            const data = await this.callAPI('/config/' + this._stored_pkey + (domain_id ? '/' + domain_id : ''), {
                method: 'GET',
                headers: {'Content-Type': 'application/json'},
            });
            this._cached_config = data;
            this._config_request = false;
            return data;
        } catch (err) {
            this._config_request = false;
            throw err;
        }
    }

    /**
     * Retrieve all translations for a non translated url
     *
     * @param url
     *
     * @return {Promise<*>}
     */
    async getUrlTranslations(language, url)
    {
        if (!this._stored_pkey) {
            throw new Error('Linguise: No available key can be used');
        }
        return await this.callAPI('/translations/url/' + this._stored_pkey + '/' + language + '/' + md5.hash(decodeURI(url)) , {
            method: 'GET',
            headers: {'Content-Type': 'application/json'},
        });
    }

    /**
     * Make API call
     *
     * @param url
     * @param options
     *
     * @returns {Promise<*>}
     */
    async callAPI(url, options = {}) {
        const final_url = new URL('http' + (process.env.API_USE_HTTPS==='1'?'s':'') + '://' +  process.env.API_DOMAIN + (process.env.API_DOMAIN_PORT?':'+process.env.API_DOMAIN_PORT:'') + process.env.API_BASE_PATH + url);

        const headers = {};
        if (['POST', 'PUT'].indexOf(options.method) !== -1 ) {
            headers['Content-Type'] = 'application/json';
        }
        options = {
            ...options,
            referrerPolicy: 'origin', // Needed to keep only the domain in the referer
            headers: new Headers(headers)
        };

        const response = await this.linguiseFetch(final_url, options);

        const json_response = await response.json();

        if (json_response.success === false) {
            throw json_response;
        }

        return json_response.data;
    }

    /**
     * Given a base URL and a current URL, return the requested path to translate.
     * This method is used to determine the path to translate, taking into account
     * the base path and the language code.
     *
     * @param {string} current_url - The current URL
     * @param {string} base_url - The base URL
     * @returns {string} The requested path to translate
     */
    getRequestedPath(current_url, base_url, language) {
        const base_path = new URL(base_url);

        let requested_path = new URL(current_url).pathname;
        if (base_path.pathname === '/' && requested_path.startsWith(`/${language}`))  {
            const temp_trim = requested_path.replace(new RegExp(`^\/${language}`), '');
            if (temp_trim.trim() === '' || temp_trim.trim() === '/') {
                requested_path = '/';
            } else {
                requested_path = temp_trim;
            }
        } else if (base_path.pathname !== '/') {
            requested_path = requested_path.replace(base_path.pathname, '');
            const temp_trim = requested_path.replace(new RegExp(`^\/${language}`), '');
            if (temp_trim.trim() === '' || temp_trim.trim() === '/') {
                requested_path = '/';
            } else {
                requested_path = temp_trim;
            }
        }

        return requested_path;
    }

    /**
     * Execute translation process.
     */
    async translate(base_url, language, html_to_translates, api_endpoint = null, with_editor = false) {
        if (api_endpoint === null) {
            api_endpoint = new URL('http' + (process.env.API_TRANSLATE_HTTPS === '1' ? 's' : '') + '://' +  process.env.API_TRANSLATE_HOST + (process.env.API_TRANSLATE_PORT ? ':' + process.env.API_TRANSLATE_PORT : '')); 
        }
        if (!this._stored_pkey) {
            throw new Error('Linguise: No available key can be used');
        }

        // use form data
        const form_data = new FormData();
        const original_url = new URL(window.location.href);

        form_data.append('version', VERSION);
        form_data.append('url', base_url);
        form_data.append('original_url', `${original_url.protocol}//${original_url.host}`)
        form_data.append('language', language);
        form_data.append('requested_path', this.getRequestedPath(window.location.href, base_url, language));
        form_data.append('content', html_to_translates);
        form_data.append('public_key', this._stored_pkey);
        form_data.append('response_code', '200');
        // form_data.append('ip', 'dynamic');
        form_data.append('user_agent', navigator.userAgent);

        // Attach editor token if available
        if (with_editor) {
            const editor_token = await Helper.tryGetLinguiseEditorCookie();
            if (editor_token) {
                form_data.append('editor_token', editor_token);
            }
        }

        const sha256Wrapper = async (key, nonce, language) => {
            const hash = new Sha256();
            hash.update(`${key}_${nonce}-${language}`);
            const digest = await hash.digest();
            return Array.from(digest).map((i) => i.toString(16).padStart(2, '0')).join('');
        }

        // anti abuse headers
        const nonce = new Date().getTime().toString();
        const headers = {
            // Referer need to set to href since some framework like Vue.js
            // does not set the referer header properly.
            'Referer': window.location.href,
            'X-Linguise-Nonce': nonce,
            'X-Linguise-Key': this._stored_pkey,
            'X-Linguise-Language': language,
            'X-Linguise-Hash': await sha256Wrapper(this._stored_pkey, nonce, language),
        }

        const response = await this.linguiseFetch(api_endpoint, {
            method: "POST",
            body: form_data,
            headers: headers,
        });

        const json_response = await response.json();

        if (json_response.response_code !== undefined && json_response.response_code !== 200) {
            throw new Error(json_response.message);
        }

        // return all since it include content/content_type/url_translations/urls_untranslated
        return json_response;
    }

}

const communication = new Communication();
export default communication;
